/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useForm } from "react-hook-form";
import { When } from "react-if";
import {
  Button,
  Input,
  Spinner,
  FlexContainer,
  Text,
  Description,
  theme,
} from "@nordcloud/gnui";
import { EditButton } from "~/components/Buttons";
import { useToggle } from "~/hooks";
import { useBillingPeriod } from "~/services/customers";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Props = {
  property: "description" | "displayName" | "name" | "type";
  type: "application" | "environment" | "orgUnitV2";
  query: any;
  mutation: any;
  queryVariables: { id: string; year?: string; [key: string]: unknown };
  required?: boolean;
  onUpdate?: () => void;
};

export function InlineText({
  query,
  mutation,
  type,
  property,
  queryVariables,
  required = true,
  onUpdate,
}: Props) {
  const [isEditMode, toggleEditMode] = useToggle(false);

  const {
    register,
    handleSubmit,

    formState: { errors },
  } = useForm();

  const { isCurrentBillingPeriod } = useBillingPeriod();

  const [update, { loading: mutationLoading }] = mutation();

  const {
    data,
    loading,
    refetch: refetchQuery,
  } = query({
    variables: queryVariables,
  });

  const onEdit = async (values: any) => {
    try {
      toggleEditMode();
      const { id, ...input } = values;

      await update({
        variables: {
          nid: id,
          input: {
            ...input,
            ...(["application", "environment"].includes(type) &&
            property === "name"
              ? { displayName: input.name }
              : {}),
          },
        },
      });

      const afterUpdateAction = onUpdate ?? refetchQuery;
      await afterUpdateAction();

      showSuccess(
        generateActionSuccessText(`${type} ${property}`)()("updated")()
      );
    } catch (error) {
      showError(error.message, {
        autoClose: false,
      });
    }
  };

  const EditForm = (
    <form css={{ display: "block" }} onSubmit={handleSubmit(onEdit)}>
      <input
        type="hidden"
        {...register("id")}
        defaultValue={queryVariables.id}
      />
      <FlexContainer>
        <Input
          type="text"
          {...register(property, { required })}
          small
          status={errors.name && "error"}
          defaultValue={data?.[type]?.[property] || ""}
        />
        <Button
          ml={theme.spacing.spacing02}
          type="submit"
          icon="save"
          size="md"
          severity="low"
        >
          Save
        </Button>
        <Button
          ml={theme.spacing.spacing01}
          icon="close"
          size="md"
          severity="low"
          onClick={toggleEditMode}
        >
          Cancel
        </Button>
      </FlexContainer>
      {required && errors[property] && (
        <Description>This field is required.</Description>
      )}
    </form>
  );

  const DisplayText = (
    <>
      <Text as="div" m="0" mr={theme.spacing.spacing02}>
        {data?.[type]?.[property] || ""}
      </Text>
      <When condition={isCurrentBillingPeriod}>
        <EditButton onClick={toggleEditMode} />
      </When>
    </>
  );

  const isLoading = loading || mutationLoading;

  if (isLoading) {
    return (
      <>
        <Spinner size="sm" />
        <Text weight="regular" tag="div" ml={theme.spacing.spacing04}>
          Loading...
        </Text>
      </>
    );
  }

  if (isEditMode) {
    return EditForm;
  }

  return <FlexContainer alignItems="center">{DisplayText}</FlexContainer>;
}
